/**
 * useSpinGamificationModel — Execute a spin on a WHEEL_SPIN gamification model
 *
 * POST /gamification-models/:id/spin
 * Returns the spin result including the winning segment, rotation degrees,
 * rewards granted, and updated spin counts. Invalidates the model cache after
 * the animation completes.
 */
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";
import {GamificationApi} from "@devour/client";
import {GAMIFICATION_MODEL_QUERY_KEY} from "./useGetGamificationModel";

export interface SpinResult {
    outcomeIndex: number;
    outcomeLabel: string;
    outcomeColor: string;
    rotationDegrees: number;
    spinsRemaining: number;
    totalSpins: number;
    rewardsGranted: boolean;
    rewards: Record<string, unknown>[];
    /*
     * The winning slice's EFFECT, for the three outcomes that are not grants:
     * one doubles the member's next earn, one hands the spin back, one takes
     * points away. Without these a "2x next" win reports as a win of nothing.
     */
    effect?: "POINT_MULTIPLIER" | "BONUS_SPIN" | "POINT_PENALTY";
    /** POINT_MULTIPLIER — epoch ms the doubled window closes. */
    multiplierExpiresAt?: number;
    /** POINT_PENALTY — points taken off. */
    pointsRemoved?: number;
}

export function useSpinGamificationModel(modelId: string) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (): Promise<SpinResult> =>
            new GamificationApi(getConfig(fullToken)).createGamificationModelsIdSpin({id: modelId}) as Promise<SpinResult>,
        onSuccess: () => {
            // Invalidate after animation completes (~3.5s)
            setTimeout(() => {
                queryClient.invalidateQueries({queryKey: [GAMIFICATION_MODEL_QUERY_KEY, modelId]});
                /* A spin can change points — refresh the board and rank directly rather than waiting on the socket. */
                queryClient.invalidateQueries({queryKey: ["ledger-standings"]});
                queryClient.invalidateQueries({queryKey: ["ledger-my-contributions"]});
                queryClient.invalidateQueries({queryKey: ["drop-achievements"]});
            }, 3500);
        },
    });
}
